import type { CadTransaction } from '../types';
import { createHistoryChecksum } from './HistoryChecksum';
import type {
  HistoryBlobDescriptor,
  HistoryBlobWriteRequest,
  HistoryCheckpointRecord,
  HistoryPersistence,
  HistoryPersistenceTransactionRecord,
} from './HistoryPersistenceTypes';

/**
 * 历史事务提交协议输入。
 */
export interface CommitHistoryTransactionWithProtocolInput {
  /** 持久化端口。 */
  readonly persistence: HistoryPersistence;
  /** 待提交 CAD 事务。 */
  readonly transaction: CadTransaction;
  /** 可选大型 blob。 */
  readonly blobs?: readonly HistoryBlobWriteRequest[];
  /** 可选 checkpoint blob。 */
  readonly checkpointBlob?: HistoryBlobWriteRequest;
  /** checkpoint ID；提供 checkpointBlob 时必填。 */
  readonly checkpointId?: string;
  /** undo 光标序号；未传时默认等于事务 seq。 */
  readonly undoCursorSeq?: number;
  /** 当前时间函数，测试时可注入。 */
  readonly now?: () => number;
}

/**
 * 历史事务提交协议结果。
 */
export interface CommitHistoryTransactionWithProtocolResult {
  /** committed 持久化事务记录。 */
  readonly transactionRecord: HistoryPersistenceTransactionRecord;
  /** 写入成功的 blob 描述。 */
  readonly blobRefs: readonly HistoryBlobDescriptor[];
  /** 写入成功的 checkpoint。 */
  readonly checkpointRecord?: HistoryCheckpointRecord;
}

/**
 * 断言 blob 写入能力存在。
 *
 * @param persistence 持久化端口。
 * @returns blob 写入函数。
 */
function assertBlobWriter(
  persistence: HistoryPersistence,
): NonNullable<HistoryPersistence['writeBlob']> {
  if (!persistence.writeBlob) {
    throw new Error('HistoryPersistence 未提供 writeBlob，不能写入历史 blob 或 checkpoint。');
  }

  return persistence.writeBlob;
}

/**
 * 写入所有关联 blob。
 *
 * @param persistence 持久化端口。
 * @param blobs 待写入 blob。
 * @returns blob 描述列表。
 */
async function writeHistoryBlobs(
  persistence: HistoryPersistence,
  blobs: readonly HistoryBlobWriteRequest[],
): Promise<readonly HistoryBlobDescriptor[]> {
  if (blobs.length === 0) {
    return [];
  }

  const writeBlob = assertBlobWriter(persistence);
  const refs: HistoryBlobDescriptor[] = [];

  for (const blob of blobs) {
    refs.push(await writeBlob(blob));
  }

  return refs;
}

/**
 * 可选写入 checkpoint。
 *
 * @param input 提交协议输入。
 * @param createdAt 创建时间。
 * @returns checkpoint 记录。
 */
async function writeOptionalCheckpoint(
  input: CommitHistoryTransactionWithProtocolInput,
  createdAt: number,
): Promise<HistoryCheckpointRecord | undefined> {
  if (!input.checkpointBlob) {
    return undefined;
  }

  if (!input.checkpointId) {
    throw new Error('写入 checkpointBlob 时必须提供 checkpointId。');
  }

  if (!input.persistence.writeCheckpoint) {
    throw new Error('HistoryPersistence 未提供 writeCheckpoint，不能写入 checkpoint。');
  }

  const writeBlob = assertBlobWriter(input.persistence);
  const blobRef = await writeBlob(input.checkpointBlob);
  const checkpointRecord: HistoryCheckpointRecord = {
    documentId: input.transaction.documentId,
    checkpointId: input.checkpointId,
    seq: input.transaction.seq,
    historySeq: input.transaction.historySeq,
    checksum: createHistoryChecksum({
      transactionId: input.transaction.id,
      blobRef,
    }),
    blobRef,
    createdAt,
  };

  await input.persistence.writeCheckpoint(checkpointRecord);

  return checkpointRecord;
}

/**
 * 按 pending -> blob/checkpoint -> checksum -> committed -> cursor 的顺序提交历史事务。
 *
 * @param input 提交协议输入。
 * @returns 提交结果。
 * @throws 当历史持久层任一步骤失败时抛出错误；若 cursor 写入与事务回滚均失败，则抛出包含两者信息的汇总错误。
 */
export async function commitHistoryTransactionWithProtocol(
  input: CommitHistoryTransactionWithProtocolInput,
): Promise<CommitHistoryTransactionWithProtocolResult> {
  const now = input.now ?? Date.now;
  const createdAt = now();
  const pendingRecord: HistoryPersistenceTransactionRecord = {
    documentId: input.transaction.documentId,
    transactionId: input.transaction.id,
    seq: input.transaction.seq,
    historySeq: input.transaction.historySeq,
    status: 'pending',
    transaction: input.transaction,
    checksum: createHistoryChecksum(input.transaction),
    blobRefs: [],
    createdAt,
    updatedAt: createdAt,
  };

  await input.persistence.writePendingTransaction(pendingRecord);

  const blobRefs = await writeHistoryBlobs(input.persistence, input.blobs ?? []);
  const checkpointRecord = await writeOptionalCheckpoint(input, createdAt);
  const committedAt = now();
  const checksum = createHistoryChecksum({
    transaction: input.transaction,
    blobRefs,
    checkpointId: checkpointRecord?.checkpointId,
  });

  await input.persistence.markTransactionCommitted({
    documentId: input.transaction.documentId,
    transactionId: input.transaction.id,
    checksum,
    blobRefs,
    checkpointId: checkpointRecord?.checkpointId,
    updatedAt: committedAt,
  });

  try {
    await input.persistence.updateDocumentCursor({
      documentId: input.transaction.documentId,
      currentSeq: input.transaction.seq,
      undoCursorSeq: input.undoCursorSeq ?? input.transaction.seq,
      latestCheckpointSeq: checkpointRecord?.seq,
      updatedAt: committedAt,
    });
  } catch (error) {
    /**
     * committed journal 只有在 cursor 同步成功后才算完整事务。
     * 如果 cursor 写失败，必须立刻把事务标记为 discarded，避免刷新前后读取到幽灵历史。
     */
    try {
      await input.persistence.markTransactionDiscarded(
        input.transaction.documentId,
        input.transaction.id,
        now(),
      );
    } catch (discardError) {
      const cursorMessage = error instanceof Error ? error.message : String(error);
      const discardMessage = discardError instanceof Error ? discardError.message : String(discardError);
      throw new Error(`历史 cursor 写入失败，且 committed 事务回滚失败：cursor=${cursorMessage}；discard=${discardMessage}`);
    }
    throw error;
  }

  return {
    transactionRecord: {
      ...pendingRecord,
      status: 'committed',
      checksum,
      blobRefs,
      checkpointId: checkpointRecord?.checkpointId,
      updatedAt: committedAt,
    },
    blobRefs,
    checkpointRecord,
  };
}

